import { useCallback } from 'react';

import useRequestJSON, {
  RunnerSegment,
  RequestRunner,
  IsLoadingSegment
} from './hooks/useRequestJSON';
import { PaginatedParams, PostJSON } from './request';

import { IAlert } from './alert';
import { IPatient } from './patient';
import { IContact } from './contact';
import { Invalidation } from './invalidation';
import moment from 'moment-timezone';
import { IEpxDepressPHQ } from './apps/epxdepress';

export interface ISession<TData = any, TSchedule = any> {
  id?: number;
  time: Date;
  respondedAt?: Date;
  isCanceled?: boolean;
  canceledAt?: Date;
  // Associations
  patientId: number;
  contactId: number;
  scheduleId?: number;
  // Attachments
  alert?: IAlert;
  alerts?: IAlert[]; // Array of alerts (for schema-based programs with multiple alerts)
  patient?: IPatient;
  contact?: IContact;
  schedule?: TSchedule;
  invalidation?: Invalidation;
  answers?: IEpxDepressPHQ['answers'];
  // For Typescript type inference
  data: TData;
}

export interface AppSettings {
  alerts?: boolean;
  afterHourInfo?: boolean;
}

export interface SingleCommunication {
  messageId?: number;
  callId?: number;
}

export interface MultipleCommunications {
  messageIds?: number[];
  callIds?: number[];
}

export const TriStateLiteral = {
  [-1]: 'Worse',
  0: 'Same',
  1: 'Better'
};

export const TriStateColor = {
  [-1]: 'intent-danger',
  0: null,
  1: 'intent-success'
};

export const YesNoLiteral = (value: boolean): string => (value ? 'Yes' : 'No');

export const YesNoColor = (value: boolean): string =>
  value ? null : 'intent-danger';

// Requests

export type SessionTransformer<R, T extends ISession> = (rawSession: R) => T;

// Pagination-based fetching

export interface GetSessionsParams extends PaginatedParams {
  includeFuture?: boolean;
  includeCanceled?: boolean;
  patient: number;
  programId?: number | null;
  appKey?: string | null;
  apps?: string[];
}

interface GetSessionsResponse<T extends ISession> {
  status: string;
  sessions: T[];
  total: number;
  phqs?: T[]; // PHQ-9 Surveys
  gads?: T[]; // GAD Surveys
  covids?: T[]; // COVID BH Surveys
}

type SessionsData<T extends ISession> = Omit<GetSessionsResponse<T>, 'status'>;

const transformer = <T extends ISession>(
  response: GetSessionsResponse<T>
): SessionsData<T> => {
  let sessions = response.sessions;
  let total = response.total;
  if (response.phqs) {
    // PHQ-9 Surveys
    sessions = response.phqs;
    total = sessions.length;
  } else if (response.gads) {
    // GAD Surveys
    sessions = response.gads;
    total = sessions.length;
  } else if (response.covids) {
    // COVID BH Surveys
    sessions = response.covids;
    total = sessions.length;
  }
  return {
    sessions: sessions,
    total: total
  };
};

export const useGetSessions = <T extends ISession, R extends ISession = never>(
  url: string,
  params?: GetSessionsParams,
  transformSession?: SessionTransformer<R, T>
): SessionsData<T> & RunnerSegment & IsLoadingSegment => {
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: params
      }),
    [
      url,
      params.page,
      params.limit,
      params.includeCanceled,
      params.includeFuture,
      params.patient,
      params.programId,
      params.appKey,
      JSON.stringify(params.apps || [])
    ]
  );
  let data: SessionsData<T> = null;
  let run: RequestRunner = null;
  let isLoading = null;
  if (transformSession) {
    const {
      data: _data,
      run: _run,
      isLoading: _isLoading
    } = useRequestJSON<GetSessionsResponse<R>, SessionsData<R>>({
      request,
      transformFn: transformer
    });
    run = _run;
    isLoading = _isLoading;
    if (_data) {
      const sessions: T[] = [];
      for (const rawSession of _data.sessions) {
        sessions.push(transformSession(rawSession));
      }
      data = {
        sessions,
        total: _data.total
      };
    }
  } else {
    const {
      data: _data,
      run: _run,
      isLoading: _isLoading
    } = useRequestJSON<GetSessionsResponse<T>, SessionsData<T>>({
      request,
      transformFn: transformer
    });
    data = _data;
    run = _run;
    isLoading = _isLoading;
  }
  return {
    sessions: data?.sessions || data?.phqs || data?.gads || [],
    total: data?.total || 0,
    run: () => run(request),
    isLoading
  };
};

// Time-range based fetching

export interface GetSessionsByRangeParams {
  app: string;
  start?: Date;
  end?: Date;
  tz?: string;
  patient: number;
  programId?: number | null;
}

interface GetSessionsByRangeResponse<T extends ISession, S = any> {
  status: string;
  sessions: T[];
  settings?: S;
}

type SessionsDataByRange<T extends ISession, S = any> = Omit<
  GetSessionsByRangeResponse<T, S>,
  'status'
>;

const transformerByRange = <T extends ISession, S = any>(
  response: GetSessionsByRangeResponse<T, S>
): SessionsDataByRange<T, S> => {
  return {
    sessions: response.sessions,
    settings: response.settings
  };
};

export const useGetSessionsByRange = <
  T extends ISession,
  R extends ISession = never,
  S = any
>(
  url: string,
  params: GetSessionsByRangeParams,
  transformSession?: SessionTransformer<R, T>,
  isDisabled?: boolean
): SessionsDataByRange<T, S> => {
  const request = useCallback(
    () =>
      PostJSON({
        url: url,
        params: {
          app: params.app,
          start: moment(params.start).format(),
          end: moment(params.end).format(),
          tz: params.tz,
          patientId: params.patient,
          programId: params.programId || null
        }
      }),
    [
      url,
      params.app,
      params.start,
      params.end,
      params.tz,
      params.patient,
      params.programId
    ]
  );
  let data: SessionsDataByRange<T, S> = null;
  if (transformSession) {
    let _data: SessionsDataByRange<R, S> = null;
    _data = useRequestJSON<
      GetSessionsByRangeResponse<R, S>,
      SessionsDataByRange<R, S>
    >({
      request,
      transformFn: transformerByRange,
      enabled: !isDisabled
    }).data;
    if (_data) {
      const sessions: T[] = [];
      for (const rawSession of _data.sessions) {
        sessions.push(transformSession(rawSession));
      }
      data = { sessions };
    }
  } else {
    data = useRequestJSON<
      GetSessionsByRangeResponse<T, S>,
      SessionsDataByRange<T, S>
    >({
      request,
      transformFn: transformerByRange
    }).data;
  }
  return {
    sessions: data?.sessions || [],
    settings: data?.settings || null
  };
};
